import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { BiSolidError } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { IoCloseSharp, IoSearchSharp } from "react-icons/io5";
import { MdOutlineEdit } from "react-icons/md";
import { RiDeleteBin6Fill } from "react-icons/ri";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import ActionMenu from "../../../component/ActionMenu";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import i18n from "../../../i8next";
import {
  useDeleteRoutesSchedulesMutation,
  useGetAllRoutesSchedulesQuery,
} from "../../../store/api/vendor/routesSchedulesSlice";

const initialPagination = {
  limit: 10,
  page: 1,
  total: 0,
  totalPages: 0,
};
const initialActions = {
  search: "",
};

const RouteSchedule = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role;
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search.trim(), 300);
  const [action, setAction] = useState(initialActions);
  const [pagination, setPagination] = useState(initialPagination);
  const [routes, setRoutes] = useState([]);
  const [id, setId] = useState("");

  // Get All Routes Schedule
  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetAllRoutesSchedulesQuery({
      page: pagination.page,
      limit: pagination.limit,
      ...action,
    });

  // delete query
  const [deleteRoutesSchedules, { isLoading: isDeleteLoading }] =
    useDeleteRoutesSchedulesMutation();

  useEffect(() => {
    if (isSuccess && data?.data) {
      setRoutes(data.data.routes);
      setPagination(data.data.pagination);
    }
  }, [data, isSuccess]);

  // search State & Debounce Setup
  useEffect(() => {
    if (debouncedSearch) {
      setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: search.trim() }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    if (!search.trim()) {
      // setPagination((prev) => ({ limit: 10, page: 1 }));
      setAction((prev) => ({ ...prev, search: "" }));
    }
  }, [search]);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit) => {
    setPagination((prev) => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setAction(initialActions);
    setPagination((prev) => ({
      ...prev,
      limit: 10,
      page: 1,
    }));
    setSearch("");
  };

  // Model Show Function
  const handleShow2 = (id) => {
    setShow(true);
    setId(id);
  };

  // Delete Route Shedule
  const handleDelete = async () => {
    toast.dismiss();
    try {
      const response = await deleteRoutesSchedules(id).unwrap();
      if (response.statusCode === 200) {
        toast.success(response.message);
        setShow(false);
        setId("");
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  // Language Change Handler
  useEffect(() => {
    const h = () => {
      refetch?.();
    };
    i18n.on("languageChanged", h);
    return () => i18n.off("languageChanged", h);
  }, [refetch]);

  // Format address to show only first part (for better display)
  const formatAddress = (address) => {
    if (!address) return "N/A";
    // Take first 30 characters or split by comma
    if (address.length > 30) {
      return address.substring(0, 30) + "...";
    }
    return address;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap w-100 mb-2">
        <div className="total">
          {t("total")}: {pagination?.total}
        </div>

        <div className="d-flex flex-wrap gap-2 ms-auto">
          <div className="d-flex flex-grow-1">
            <input
              type="text"
              className="form-control custom-field-one"
              placeholder={`${t("search")}..`}
              value={search}
              onChange={(e) => setSearch(e.target.value.trim())}
            />
          </div>

          <div className="d-flex gap-2 ms-auto">
            <button
              className="btn btn-primary addDriver"
              onClick={handleResetFilters}
            >
              {t("btns.reset")}
            </button>

            <NavLink
              to="add"
              className="page-add-btn d-flex align-items-center justify-content-center gap-2"
            >
              <FaPlus /> {t("add")} {t("route")} & {t("schedule")}
            </NavLink>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>{`${t("Sr")}. ${t("no")}.`}</th>
              <th>{`${t("route")} ${t("name")}`}</th>
              <th>{`${t("pickup")} ${t("address")}`}</th>
              <th>{`${t("dropoff")} ${t("address")}`}</th>
              <th>{t("radius")} (m)</th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading length={6} />
            ) : routes?.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  {`${t("no")} ${t("routes")} ${t("available")}`}
                </td>
              </tr>
            ) : (
              routes.map((route, i) => (
                <tr key={route.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td>
                    <div className="fw-semibold">{route.route_name}</div>
                    <div className="text-muted small mt-1">
                      {t("createdBy")}: {route.owner?.name || "N/A"}
                    </div>
                  </td>
                  <td>
                    <div
                      className="text-truncate"
                      style={{ maxWidth: "200px" }}
                      title={route.pickup_address}
                    >
                      {formatAddress(route.pickup_address)}
                    </div>
                    {route.pickup_lat && route.pickup_lng && (
                      <div className="text-muted small mt-1">
                        {parseFloat(route.pickup_lat).toFixed(6)},{" "}
                        {parseFloat(route.pickup_lng).toFixed(6)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div
                      className="text-truncate"
                      style={{ maxWidth: "200px" }}
                      title={route.dropoff_address}
                    >
                      {formatAddress(route.dropoff_address)}
                    </div>
                    {route.dropoff_lat && route.dropoff_lng && (
                      <div className="text-muted small mt-1">
                        {parseFloat(route.dropoff_lat).toFixed(6)},{" "}
                        {parseFloat(route.dropoff_lng).toFixed(6)}
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge bg-info">
                      {route.radius_meters || 0}m
                    </span>
                  </td>
                  <td>
                    <ActionMenu
                      actions={[
                        {
                          label: t("edit"),
                          icon: <MdOutlineEdit size={16} />,
                          onClick: () =>
                            navigate(`/route-and-schedule/edit/${route.id}`),
                        },
                        {
                          label: t("btns.delete"),
                          icon: <RiDeleteBin6Fill size={15} />,
                          onClick: () => handleShow2(route.id),
                          danger: true,
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Modal for Delete */}
      <Modal show={show}>
        <Button className="closePopup" onClick={() => setShow(false)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div>
            <div className="text-center mb-3 errorIcon">
              <BiSolidError />
            </div>
            <div className="text-center mb-4 deleteHd">{t("delete-modal")}</div>
            <div className="text-center">
              <Button
                className="smallBtn cancel me-3"
                onClick={() => setShow(false)}
              >
                {t("btns.cancel")}
              </Button>
              <Button
                className="smallBtn auto"
                onClick={handleDelete}
                disabled={isDeleteLoading}
              >
                {isDeleteLoading ? (
                  <div className="spinner-border" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : (
                  t("btns.delete")
                )}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RouteSchedule;
